import { useEffect, useState } from "react";
import { api, type Customer, type FollowUp, type Lead, type Project } from "./api.js";
import { useT, type MessageKey } from "./i18n/index.js";

const LEAD_STAGES = ["NEW", "CONTACTED", "QUALIFIED", "SITE_ASSESSMENT", "PROPOSAL", "NEGOTIATION", "WON", "LOST"];
const NEXT_STAGE: Record<string, string> = {
  NEW: "CONTACTED", CONTACTED: "QUALIFIED", QUALIFIED: "SITE_ASSESSMENT",
  SITE_ASSESSMENT: "PROPOSAL", PROPOSAL: "NEGOTIATION", NEGOTIATION: "WON",
};
const STATUS_CHIP: Record<string, string> = {
  FEASIBILITY: "info", DESIGN: "warn", PROPOSAL_SENT: "info", ACCEPTED: "ok",
  OPERATING: "ok", COMPLETED: "ok", CANCELLED: "bad",
};

/** Enum → catalogue key helpers (fall back to the raw enum if a key is missing). */
function enumKey(prefix: "pstatus" | "ptype" | "lstage", v: string): MessageKey {
  return `${prefix}.${v}` as MessageKey;
}

export default function Dashboard({ onOpenProject }: { onOpenProject: (id: string, name: string) => void }): JSX.Element {
  const t = useT();
  const [tab, setTab] = useState<"projects" | "pipeline" | "widget" | "prices" | "integrations">("projects");
  const [projects, setProjects] = useState<Project[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [board, setBoard] = useState<{ leads: Record<string, Lead[]>; projects: Record<string, Project[]> } | null>(null);
  const [q, setQ] = useState("");
  const [error, setError] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [openLead, setOpenLead] = useState<string | null>(null);
  const [widgetKey, setWidgetKey] = useState("");

  async function reload(): Promise<void> {
    try {
      const [p, c, b] = await Promise.all([api.projects.list(), api.customers.list(), api.pipeline()]);
      setProjects(p);
      setCustomers(c);
      setBoard(b);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }
  useEffect(() => {
    void reload();
    api.org().then((o) => setWidgetKey(o.widgetKey)).catch(() => undefined);
  }, []);

  const filtered = projects.filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase()));
  const custName = (id?: string | null) => customers.find((c) => c.id === id)?.lastName ?? "—";

  return (
    <div className="dash">
      <aside className="dash-side">
        <div className={`ds-item ${tab === "projects" ? "active" : ""}`} onClick={() => setTab("projects")}>
          <div>📋 {t("dash.tab.projects")}<span>{t("dash.tab.projects.sub")}</span></div>
        </div>
        <div className={`ds-item ${tab === "pipeline" ? "active" : ""}`} onClick={() => setTab("pipeline")}>
          <div>📊 {t("dash.tab.pipeline")}<span>{t("dash.tab.pipeline.sub")}</span></div>
        </div>
        <div className={`ds-item ${tab === "widget" ? "active" : ""}`} onClick={() => setTab("widget")}>
          <div>🧲 {t("dash.tab.widget")}<span>{t("dash.tab.widget.sub")}</span></div>
        </div>
        <div className={`ds-item ${tab === "prices" ? "active" : ""}`} onClick={() => setTab("prices")}>
          <div>💶 {t("dash.tab.prices")}<span>{t("dash.tab.prices.sub")}</span></div>
        </div>
        <div className={`ds-item ${tab === "integrations" ? "active" : ""}`} onClick={() => setTab("integrations")}>
          <div>🔌 {t("dash.tab.integrations")}<span>{t("dash.tab.integrations.sub")}</span></div>
        </div>
      </aside>
      <main className="dash-main">
        {error && <div className="msg err">{error}</div>}
        {tab === "integrations" ? (
          <IntegrationsTab />
        ) : tab === "prices" ? (
          <PriceBookTab />
        ) : tab === "widget" ? (
          <WidgetTab widgetKey={widgetKey} />
        ) : tab === "projects" ? (
          <>
            <h1>{t("dash.myProjects")}</h1>
            <div className="kpis">
              <div className="kpi"><div className="l">{t("dash.kpi.projects")}</div><div className="v">{projects.length}</div></div>
              <div className="kpi"><div className="l">{t("dash.kpi.customers")}</div><div className="v">{customers.length}</div></div>
              <div className="kpi"><div className="l">{t("dash.kpi.openLeads")}</div><div className="v">{board ? Object.entries(board.leads).filter(([s]) => s !== "WON" && s !== "LOST").reduce((n, [, l]) => n + l.length, 0) : "–"}</div></div>
              <div className="kpi"><div className="l">{t("dash.kpi.accepted")}</div><div className="v">{projects.filter((p) => ["ACCEPTED", "OPERATING", "COMPLETED"].includes(p.status)).length}</div></div>
            </div>
            <div className="toolbar">
              <button className="btn btn-primary" onClick={() => setShowNew(true)}>{t("dash.addProject")}</button>
              <input type="text" placeholder={t("dash.search")} value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr><th>{t("dash.col.name")}</th><th>{t("dash.col.customer")}</th><th>{t("dash.col.type")}</th><th>{t("dash.col.status")}</th><th>{t("dash.col.updated")}</th></tr>
                </thead>
                <tbody>
                  {filtered.map((p) => (
                    <tr key={p.id} className="row" onClick={() => onOpenProject(p.id, p.name)}>
                      <td><b>{p.name}</b></td>
                      <td>{custName(p.customerId)}</td>
                      <td className="muted">{t(enumKey("ptype", p.projectType))}</td>
                      <td><span className={`chip ${STATUS_CHIP[p.status] ?? ""}`}>{t(enumKey("pstatus", p.status))}</span></td>
                      <td className="muted">{new Date(p.updatedAt).toLocaleDateString("de-DE")}</td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={5} className="muted" style={{ textAlign: "center", padding: 26 }}>{t("dash.empty")}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <>
            <h1>{t("dash.salesPipeline")}</h1>
            <div className="muted" style={{ margin: "4px 0 16px" }}>{t("dash.pipelineHint")}</div>
            <div className="board">
              {LEAD_STAGES.map((stage) => (
                <div className="board-col" key={stage}>
                  <h4>{t(enumKey("lstage", stage))}<span>{board?.leads[stage]?.length ?? 0}</span></h4>
                  {(board?.leads[stage] ?? []).map((l) => (
                    <div className="board-card" key={l.id} onClick={() => setOpenLead(l.id)}>
                      <b>{custName(l.customerId)}</b>
                      <span className="muted">
                        {l.estimatedKwp ? `${l.estimatedKwp} kWp · ` : ""}
                        {l.source === "WEB_CALCULATOR" ? t("dash.widgetLead") : l.source.toLowerCase()}
                      </span>
                      <div style={{ marginTop: 6, display: "flex", gap: 6 }} onClick={(e) => e.stopPropagation()}>
                        {NEXT_STAGE[stage] && (
                          <button className="btn btn-sm" onClick={() => void api.leads.update(l.id, { stage: NEXT_STAGE[stage]! }).then(reload)}>
                            → {t(enumKey("lstage", NEXT_STAGE[stage]!))}
                          </button>
                        )}
                        {stage !== "LOST" && stage !== "WON" && (
                          <button className="btn btn-sm" onClick={() => void api.leads.update(l.id, { stage: "LOST" }).then(reload)}>✕</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </>
        )}
        {showNew && <NewProjectModal customers={customers} onClose={() => setShowNew(false)} onCreated={(id, name) => { setShowNew(false); onOpenProject(id, name); }} />}
        {openLead && (
          <LeadDrawer
            leadId={openLead}
            onClose={() => { setOpenLead(null); void reload(); }}
            onProjectCreated={(id, name) => onOpenProject(id, name)}
          />
        )}
      </main>
    </div>
  );
}

/* ---------------------------- Lead detail drawer -------------------------- */

function LeadDrawer({ leadId, onClose, onProjectCreated }: {
  leadId: string;
  onClose: () => void;
  onProjectCreated: (id: string, name: string) => void;
}): JSX.Element {
  const t = useT();
  const [data, setData] = useState<{ lead: Lead; customer: Customer | null; followUps: FollowUp[] } | null>(null);
  const [dueAt, setDueAt] = useState(() => new Date(Date.now() + 2 * 86400000).toISOString().slice(0, 10));
  const [kind, setKind] = useState("call");
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");

  const load = () => api.leads.detail(leadId).then(setData).catch((e) => setErr(String(e)));
  useEffect(() => { void load(); }, [leadId]);

  async function addFollowUp(): Promise<void> {
    await api.leads.addFollowUp(leadId, { dueAt: new Date(dueAt).toISOString(), kind, note: note || undefined });
    setNote("");
    await load();
  }

  async function convert(): Promise<void> {
    if (!data) return;
    const p = await api.projects.create({
      name: `${t("lead.projectPrefix")} ${data.customer?.lastName ?? t("lead.title")}`,
      customerId: data.lead.customerId,
      leadId: data.lead.id,
    });
    onProjectCreated(p.id, p.name);
  }

  const wp = data?.lead.widgetPayload as { roofAreaM2?: number; annualConsumptionKwh?: number; batteryInterest?: boolean; estimate?: { kwp: number; savingsEurPerYear: number; paybackYears: number } } | null;

  return (
    <div style={{ position: "fixed", inset: 0, background: "var(--scrim)", zIndex: 100 }} onClick={onClose}>
      <div className="card" style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: 380, maxWidth: "92vw", borderRadius: "12px 0 0 12px", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
        {err && <div className="msg err">{err}</div>}
        {!data ? <span className="spinner" /> : (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <h2>{data.customer?.lastName ?? t("lead.title")}</h2>
                <div className="muted" style={{ fontSize: 12.5 }}>
                  {data.customer?.email ?? t("lead.noEmail")} · {data.customer?.phone ?? t("lead.noPhone")}
                </div>
              </div>
              <button className="btn btn-sm" onClick={onClose}>✕</button>
            </div>
            <div style={{ margin: "10px 0" }}>
              <span className="chip info">{t(enumKey("lstage", data.lead.stage))}</span>{" "}
              <span className="chip">{data.lead.source === "WEB_CALCULATOR" ? t("lead.widgetLead") : data.lead.source.toLowerCase()}</span>
            </div>
            {wp?.estimate && (
              <div className="msg info">
                {t("lead.widgetEstimate")} <b>{wp.estimate.kwp} kWp</b> · {t("lead.savings")} ≈ {Math.round(wp.estimate.savingsEurPerYear)} {t("lead.perYear")} · {t("lead.payback")} ≈ {wp.estimate.paybackYears} {t("lead.years")}
                {wp.roofAreaM2 ? ` · ${t("lead.roof")} ~${wp.roofAreaM2} m²` : ""}{wp.batteryInterest ? ` · ${t("lead.wantsBattery")}` : ""}
              </div>
            )}
            {data.lead.notes && <div className="msg">{data.lead.notes}</div>}
            <h3 style={{ margin: "14px 0 8px" }}>{t("lead.followUps")}</h3>
            {data.followUps.length === 0 && <div className="muted" style={{ fontSize: 12.5, marginBottom: 8 }}>{t("lead.noneYet")}</div>}
            {data.followUps.map((f) => (
              <div key={f.id} className={`check ${f.doneAt ? "donec" : ""}`}>
                <div className="cb" onClick={() => void api.leads.setFollowUpDone(leadId, f.id, !f.doneAt).then(load)}>
                  {f.doneAt ? "✓" : ""}
                </div>
                <div className="t">
                  <b>{f.kind} · {new Date(f.dueAt).toLocaleDateString("de-DE")}</b>
                  <span>{f.note ?? ""}{!f.doneAt && new Date(f.dueAt) < new Date() ? ` · ${t("lead.overdue")}` : ""}</span>
                </div>
              </div>
            ))}
            <div className="frow" style={{ marginTop: 8 }}>
              <div className="field"><label>{t("lead.due")}</label><input type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} /></div>
              <div className="field"><label>{t("lead.kind")}</label>
                <select value={kind} onChange={(e) => setKind(e.target.value)}>
                  <option value="call">{t("lead.kind.call")}</option><option value="email">{t("lead.kind.email")}</option>
                  <option value="visit">{t("lead.kind.visit")}</option><option value="other">{t("lead.kind.other")}</option>
                </select>
              </div>
            </div>
            <div className="field" style={{ marginTop: 8 }}><label>{t("lead.note")}</label><input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("lead.notePlaceholder")} /></div>
            <button className="btn btn-o" style={{ marginTop: 8, width: "100%" }} onClick={() => void addFollowUp()}>{t("lead.addFollowUp")}</button>
            <button className="btn btn-primary" style={{ marginTop: 14, width: "100%" }} onClick={() => void convert()}>
              {t("lead.createProject")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ------------------------------ Price book tab ---------------------------- */

const RATE_ROWS: { key: string; labelKey: MessageKey; defaultEur: number; unitKey: MessageKey }[] = [
  { key: "labor.residential", labelKey: "prices.rate.labor.residential", defaultEur: 300, unitKey: "prices.unit.perKwp" },
  { key: "labor.commercial", labelKey: "prices.rate.labor.commercial", defaultEur: 260, unitKey: "prices.unit.perKwp" },
  { key: "electrical.residential", labelKey: "prices.rate.electrical.residential", defaultEur: 200, unitKey: "prices.unit.perKwp" },
  { key: "electrical.commercial", labelKey: "prices.rate.electrical.commercial", defaultEur: 170, unitKey: "prices.unit.perKwp" },
  { key: "mounting.pitched_roof", labelKey: "prices.rate.mounting.pitched_roof", defaultEur: 130, unitKey: "prices.unit.perKwp" },
  { key: "mounting.flat_roof", labelKey: "prices.rate.mounting.flat_roof", defaultEur: 110, unitKey: "prices.unit.perKwp" },
  { key: "mounting.ground", labelKey: "prices.rate.mounting.ground", defaultEur: 85, unitKey: "prices.unit.perKwp" },
  { key: "laborPerModule", labelKey: "prices.rate.laborPerModule", defaultEur: 12, unitKey: "prices.unit.perModule" },
  { key: "scaffolding", labelKey: "prices.rate.scaffolding", defaultEur: 900, unitKey: "prices.unit.eur" },
  { key: "batteryInstall", labelKey: "prices.rate.batteryInstall", defaultEur: 800, unitKey: "prices.unit.eur" },
  { key: "planning.residential", labelKey: "prices.rate.planning.residential", defaultEur: 900, unitKey: "prices.unit.eur" },
  { key: "margin.pct", labelKey: "prices.rate.margin.pct", defaultEur: 0, unitKey: "prices.unit.pct" },
  { key: "regional.factor", labelKey: "prices.rate.regional.factor", defaultEur: 1, unitKey: "prices.unit.factor" },
];

function PriceBookTab(): JSX.Element {
  const t = useT();
  const [entries, setEntries] = useState<Record<string, number>>({});
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [components, setComponents] = useState<{ kind: string; id: string; label: string; listPriceEur: number }[]>([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api.priceBook.get().then((r) => setEntries(Object.fromEntries(r.entries.map((e) => [e.key, e.valueEur]))));
    void api.components().then((c) =>
      setComponents([
        ...c.modules.map((m) => ({ kind: "module", id: m.id, label: `${m.manufacturer} ${m.model}`, listPriceEur: m.listPriceEur })),
        ...c.inverters.map((i) => ({ kind: "inverter", id: i.id, label: `${i.manufacturer} ${i.model}`, listPriceEur: i.listPriceEur })),
        ...c.batteries.map((b) => ({ kind: "battery", id: b.id, label: `${b.manufacturer} ${b.model}`, listPriceEur: b.listPriceEur })),
      ]),
    );
  }, []);

  function setDraft(key: string, v: string): void {
    setDrafts((d) => ({ ...d, [key]: v }));
  }

  async function save(): Promise<void> {
    const changed = Object.entries(drafts).map(([key, v]) => ({
      key,
      valueEur: v.trim() === "" ? null : Number(v.replace(",", ".")),
    })).filter((e) => e.valueEur === null || Number.isFinite(e.valueEur));
    if (changed.length === 0) return;
    setBusy(true);
    setNote("");
    try {
      const r = await api.priceBook.put(changed);
      setEntries(Object.fromEntries(r.entries.map((e) => [e.key, e.valueEur])));
      setDrafts({});
      setNote(t("prices.saved"));
    } catch (e) {
      setNote(`${t("common.error")}: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  const cell = (key: string, fallback: number | "") => (
    <input
      type="text"
      style={{ width: 110, textAlign: "right" }}
      placeholder={fallback === "" ? "" : String(fallback)}
      value={drafts[key] ?? (entries[key] !== undefined ? String(entries[key]) : "")}
      onChange={(e) => setDraft(key, e.target.value)}
    />
  );

  return (
    <div>
      <h1>{t("prices.title")}</h1>
      <div className="muted" style={{ margin: "4px 0 16px", maxWidth: 720 }}>
        {t("prices.hint")}
      </div>
      {note && <div className={`msg ${note.startsWith(t("common.error")) ? "err" : "ok"}`}>{note}</div>}
      <div className="card" style={{ maxWidth: 760, marginBottom: 16 }}>
        <h3 style={{ marginBottom: 10 }}>{t("prices.ratesTitle")}</h3>
        <table className="tbl">
          <tbody>
            {RATE_ROWS.map((r) => (
              <tr key={r.key}>
                <td>{t(r.labelKey)}</td>
                <td className="muted">{t(r.unitKey)}</td>
                <td className="r">{cell(r.key, r.defaultEur)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="card" style={{ maxWidth: 760, marginBottom: 16 }}>
        <h3 style={{ marginBottom: 10 }}>{t("prices.componentsTitle")}</h3>
        <table className="tbl">
          <thead><tr><th>{t("prices.col.component")}</th><th className="r">{t("prices.col.list")}</th><th className="r">{t("prices.col.yours")}</th></tr></thead>
          <tbody>
            {components.map((c) => (
              <tr key={`${c.kind}:${c.id}`}>
                <td>{c.label} <span className="muted" style={{ fontSize: 11 }}>({c.kind})</span></td>
                <td className="r muted">{c.listPriceEur.toLocaleString("de-DE")}</td>
                <td className="r">{cell(`${c.kind}:${c.id}`, "")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button className="btn btn-primary" disabled={busy || Object.keys(drafts).length === 0} onClick={() => void save()}>
        {busy ? <span className="spinner" /> : `${t("prices.save")} ${Object.keys(drafts).length || ""} ${t("prices.changes")}`}
      </button>
    </div>
  );
}

/* ---------------------------- Widget embed tab ---------------------------- */

function WidgetTab({ widgetKey }: { widgetKey: string }): JSX.Element {
  const t = useT();
  const apiOrigin = window.location.origin;
  const embed = `<div id="pv-widget" data-key="${widgetKey || "…"}" data-api="${apiOrigin}"></div>\n<script src="${apiOrigin}/pv-widget.js" defer></script>`;
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <h1>{t("widget.title")}</h1>
      <div className="muted" style={{ margin: "4px 0 16px" }}>
        {t("widget.hint")}
      </div>
      <div className="card" style={{ maxWidth: 720 }}>
        <h3 style={{ marginBottom: 8 }}>{t("widget.embedTitle")}</h3>
        <pre style={{ background: "var(--code-bg)", padding: 12, borderRadius: 8, fontSize: 12, overflowX: "auto" }}>{embed}</pre>
        <button
          className="btn btn-o btn-sm"
          onClick={() => { void navigator.clipboard.writeText(embed).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }); }}
        >
          {copied ? t("widget.copied") : t("widget.copy")}
        </button>
        <div className="msg info" style={{ marginTop: 12 }}>
          {t("widget.keyLabel")} <b>{widgetKey || t("widget.loading")}</b> {t("widget.keyHint")}
        </div>
        <div className="msg info">
          {t("widget.demoHint1")} <code>apps/widget/demo.html</code> {t("widget.demoHint2")} (<code>npm run build</code> {t("widget.demoHint3")} <code>apps/widget</code>).
        </div>
      </div>
    </div>
  );
}

function NewProjectModal({ customers, onClose, onCreated }: {
  customers: Customer[];
  onClose: () => void;
  onCreated: (id: string, name: string) => void;
}): JSX.Element {
  const t = useT();
  const [name, setName] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [newCustomer, setNewCustomer] = useState("");
  const [projectType, setProjectType] = useState("RESIDENTIAL_PITCHED");
  const [withLead, setWithLead] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function create(): Promise<void> {
    setError("");
    setBusy(true);
    try {
      let cid = customerId;
      if (!cid && newCustomer.trim()) {
        const c = await api.customers.create({ lastName: newCustomer.trim() });
        cid = c.id;
      }
      let leadId: string | undefined;
      if (withLead && cid) {
        const lead = await api.leads.create({ customerId: cid, source: "MANUAL" });
        leadId = lead.id;
      }
      const p = await api.projects.create({ name: name || t("newproject.defaultName"), customerId: cid || undefined, leadId, projectType });
      onCreated(p.id, p.name);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "var(--scrim-strong)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div className="card" style={{ width: 480, maxWidth: "94vw" }}>
        <h2 style={{ marginBottom: 14 }}>{t("newproject.title")}</h2>
        <div className="field" style={{ marginBottom: 10 }}>
          <label>{t("newproject.name")}</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("newproject.namePlaceholder")} />
        </div>
        <div className="frow" style={{ marginBottom: 10 }}>
          <div className="field">
            <label>{t("newproject.customer")}</label>
            <select value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
              <option value="">{t("newproject.newCustomer")}</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>{c.lastName}{c.firstName ? `, ${c.firstName}` : ""}</option>
              ))}
            </select>
          </div>
          {!customerId && (
            <div className="field">
              <label>{t("newproject.newCustomerName")}</label>
              <input value={newCustomer} onChange={(e) => setNewCustomer(e.target.value)} placeholder={t("newproject.newCustomerPlaceholder")} />
            </div>
          )}
        </div>
        <div className="field" style={{ marginBottom: 10 }}>
          <label>{t("newproject.type")}</label>
          <select value={projectType} onChange={(e) => setProjectType(e.target.value)}>
            <option value="RESIDENTIAL_PITCHED">{t("ptype.RESIDENTIAL_PITCHED")}</option>
            <option value="RESIDENTIAL_FLAT">{t("ptype.RESIDENTIAL_FLAT")}</option>
            <option value="COMMERCIAL_FLAT">{t("ptype.COMMERCIAL_FLAT")}</option>
            <option value="INDUSTRIAL">{t("ptype.INDUSTRIAL")}</option>
            <option value="GROUND_MOUNTED">{t("ptype.GROUND_MOUNTED")}</option>
            <option value="CARPORT">{t("ptype.CARPORT")}</option>
          </select>
        </div>
        <label style={{ fontSize: 12.5, display: "flex", gap: 7, alignItems: "center", marginBottom: 12 }}>
          <input type="checkbox" checked={withLead} onChange={(e) => setWithLead(e.target.checked)} style={{ width: "auto" }} />
          {t("newproject.alsoLead")}
        </label>
        {error && <div className="msg err">{error}</div>}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button className="btn" onClick={onClose}>{t("newproject.cancel")}</button>
          <button className="btn btn-primary" disabled={busy} onClick={() => void create()}>
            {busy ? <span className="spinner" /> : t("newproject.create")}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ------------------------- Integrations (Data Sources) ------------------- */

function IntegrationsTab(): JSX.Element {
  const t = useT();
  const [data, setData] = useState<{ view: "member" | "admin"; plan: string; providers: import("./api.js").IntegrationProvider[] } | null>(null);
  const [note, setNote] = useState("");
  const [keyDraft, setKeyDraft] = useState<Record<string, string>>({});

  const load = () => api.integrations.list().then(setData).catch((e) => setNote(String(e)));
  useEffect(() => { void load(); }, []);

  if (!data) return <div style={{ padding: 30 }}><span className="spinner" /></div>;
  const isAdmin = data.view === "admin";
  const badge = (p: import("./api.js").IntegrationProvider) =>
    p.status === "active" ? <span className="chip ok">{t("integrations.active")}</span>
    : p.status === "stub" ? <span className="chip">{t("integrations.coming")}</span>
    : <span className="chip info">{t("integrations.locked")}</span>;

  return (
    <>
      <h1>{t("integrations.title")}</h1>
      <div className="sub">
        {t("integrations.hint")} <b>{data.plan}</b>
      </div>
      {note && <div className="msg err">{note}</div>}
      <table className="tbl">
        <thead><tr><th>{t("integrations.col.provider")}</th><th>{t("integrations.col.type")}</th><th className="r">{t("integrations.col.confidence")}</th><th className="r">{t("integrations.col.cost")}</th><th>{t("integrations.col.status")}</th>{isAdmin && <th>{t("integrations.col.config")}</th>}</tr></thead>
        <tbody>
          {data.providers.map((p) => (
            <tr key={p.id}>
              <td>
                <b>{p.name}</b>
                <br /><span className="muted" style={{ fontSize: 10.5 }}>{p.freshness}{p.lockReason ? ` · ${p.lockReason}` : ""}</span>
              </td>
              <td><span className="chip">{p.type.replace("_", " ")}</span></td>
              <td className="r">{p.confidencePct > 0 ? `${p.confidencePct} %` : "—"}</td>
              <td className="r">{p.costLabel}</td>
              <td>{badge(p)}</td>
              {isAdmin && (
                <td style={{ whiteSpace: "nowrap" }}>
                  <label style={{ fontSize: 11.5, marginRight: 8 }}>
                    <input
                      type="checkbox"
                      checked={p.settings?.enabled ?? p.status === "active"}
                      onChange={(e) => {
                        void api.integrations.update(p.id, { enabled: e.target.checked }).then(load);
                      }}
                    /> {t("integrations.enabled")}
                  </label>
                  {p.auth === "api_key" && (
                    <>
                      <input
                        type="password"
                        placeholder={p.settings?.apiKeySet ? t("integrations.keySet") : t("integrations.keyPlaceholder")}
                        value={keyDraft[p.id] ?? ""}
                        onChange={(e) => setKeyDraft({ ...keyDraft, [p.id]: e.target.value })}
                        style={{ width: 130, padding: "3px 6px", fontSize: 11.5 }}
                      />{" "}
                      <button
                        className="btn btn-sm"
                        disabled={!keyDraft[p.id]}
                        onClick={() => {
                          void api.integrations.update(p.id, { apiKey: keyDraft[p.id]!, enabled: true }).then(() => {
                            setKeyDraft({ ...keyDraft, [p.id]: "" });
                            void load();
                          });
                        }}
                      >{t("integrations.save")}</button>
                    </>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="muted" style={{ fontSize: 11.5, marginTop: 10 }}>
        {t("integrations.lockedHint")}
      </div>
    </>
  );
}
